/**
 * One reading of an API failure, for every screen.
 *
 * Owner, 2026-08-15 and again 2026-08-25: a failed save showed
 * `Request failed with status code 400` and nothing else — the reason (an
 * 11-digit CR where 10 is required) was in the response the whole time, and
 * diagnosing it needed shell access to a production container. And when the
 * server DOES explain, it explains in English, which an Arabic operator cannot
 * read.
 *
 * Three rules follow, and they live here rather than in 951 toast call sites:
 *
 *   1. Never surface a bare HTTP status. axios puts that in `err.message`;
 *      the useful text is `err.response.data.error`.
 *   2. Prefer a CODE over prose. The portal owns the en/ar catalogue, so
 *      `t('err.VAT_NUMBER_INVALID')` renders in the operator's language.
 *      When a code has no key yet, fall back to the server's English rather
 *      than to a humanised key — this portal turns a missing key into
 *      plausible-looking English, which would hide the gap.
 *   3. Return every bad field at once, so a form can highlight them together.
 */

import { permissionLabel } from './permissionLabels';

/**
 * axios's own English when the server said nothing useful: a bare status, or
 * no reply at all ("Network Error", a timeout). Never shown to a user; the
 * caller's translated fallback is.
 */
const BARE_STATUS =
  /^(Request failed with status code \d+|Network Error|timeout of \d+ms exceeded)$/i;
// Some endpoints put the CODE in the `error` field and the prose in
// `message` (AMOUNT_EXCEEDS_REMAINING, OTP_LOCKED,
// INSURER_BILLING_DETAILS_MISSING...). Read verbatim, the toast showed the
// user a SCREAMING_SNAKE token; read as a code, it becomes translatable.
const CODE_SHAPE = /^[A-Z][A-Z0-9_]{2,}$/;

// Only a STRING is a message. An endpoint shipping an object in `error` or
// `message` would otherwise travel all the way to JSX, and "Objects are not
// valid as a React child" has already taken down two screens in this change
// set. The chokepoint enforces the type rather than trusting every emitter.
const str = (v) => (typeof v === 'string' && v ? v : null);

/**
 * Normalise any thrown/rejected API failure into one shape.
 * Accepts an axios error, a rejectWithValue payload, or a plain string.
 */
/**
 * Does this key really exist? Guarded because a test may render without the
 * shared instance, and because a missing key here would render as a humanised
 * key name and read like real copy — hiding an untranslated error rather than
 * surfacing it.
 */
let cachedI18n;

const keyExists = (key) => {
  try {
    if (cachedI18n === undefined) {
      // Resolved lazily, not imported at module load. A top-level import made
      // this helper drag the whole i18n bundle into every module that touches
      // an error — and any test that mocks react-i18next then died on
      // `.use(initReactI18next)` with an undefined module, before a single
      // assertion ran. Deferring it means such a test simply gets `false` here
      // and reads the server's English, which is the correct fallback anyway.
      // eslint-disable-next-line global-require
      cachedI18n = require('../i18n').default || null;
    }
    return typeof cachedI18n?.exists === 'function' && cachedI18n.exists(key);
  } catch {
    cachedI18n = null;
    return false;
  }
};

const uiIsArabic = () => {
  keyExists('err.unexpected'); // resolves cachedI18n the same lazy way
  return String(cachedI18n?.language || '').startsWith('ar');
};

/**
 * The interpolation values for a PERMISSION_DENIED refusal.
 *
 * The server names the permission as catalog keys (`users` / `read`), which is
 * exactly what QC saw in the Arabic UI. This swaps them for the permission's
 * own label — the one the Permission Manager grid shows — in the active
 * language, and selects the `_named` sentence that carries it.
 *
 * The server's English `description` is deliberately NOT offered as a
 * fallback: `permissionLabel` would return it for a key with no translation,
 * dropping an English phrase into an Arabic sentence. No label means the
 * generic sentence, which is translated.
 */
const permissionDeniedValues = (t, data) => {
  if (!data || typeof data.resource !== 'string' || typeof data.action !== 'string') return data;
  const label = permissionLabel(
    t,
    { exists: keyExists },
    { resource: data.resource, action: data.action }
  );
  // A grid label can explain itself in a second sentence ("... (team lead).
  // Without it, staff see only ..."). Quoted as a name, only the first
  // sentence reads as one; the trailing full stop would sit inside the quotes.
  // ponytail: splits on ". " — no label carries an abbreviation today.
  const name = label.split(/\.\s/)[0].replace(/\.$/, '');
  return name ? { ...data, permission: name, context: 'named' } : data;
};

export const apiError = (err) => {
  if (!err) return { code: null, message: null, fieldErrors: [], status: null };
  if (typeof err === 'string') return { code: null, message: err, fieldErrors: [], status: null };

  // A thunk rejected with rejectWithValue hands the response body straight
  // through, so accept either shape rather than making every caller remember.
  //
  // `errors` belongs in this list for the same reason the others do. An
  // express-validator 400 rejected as `rejectWithValue(error.response.data)`
  // arrives as a bare { errors: [{ path, msg }] } — no `error`, no `code` — so
  // without it body stayed null, the validator branch below found nothing, and
  // the form showed a generic toast while the server had named the field the
  // whole time. That is the exact failure this file exists to end.
  const body =
    err.response?.data ??
    (err.error || err.fieldErrors || err.code || Array.isArray(err.errors) ? err : null);
  const status = err.response?.status ?? err.status ?? null;

  // Two shapes reach us, and both must work.
  //
  //   fieldErrors[] {field, code, message, value}   the contract errorHandler emits
  //   fields[]      {field, reason|message}         33 controllers already send this,
  //                                                 and it predates the contract
  //
  // `fields` is ALSO a string[] of human labels on the unique-constraint branch
  // ("Email address"), which is prose for a sentence, not a field name — those
  // entries are skipped rather than keyed onto an input that does not exist.
  const declared = Array.isArray(body?.fieldErrors)
    ? body.fieldErrors.filter((f) => f && (f.field || f.code || f.message))
    : [];

  const legacy = Array.isArray(body?.fields)
    ? body.fields
        .filter((f) => f && typeof f === 'object')
        .map((f) => ({
          field: f.field || null,
          code: f.code || null,
          message: f.message || f.reason || null,
          value: f.value === undefined ? null : f.value,
        }))
        .filter((f) => f.field || f.code || f.message)
    : [];

  // A third shape: express-validator's errors[], which at least nine controllers
  // return alongside code VALIDATION_FAILED. Unread, the toast said "check the
  // highlighted fields" and highlighted none, and each field's own `msg` — the
  // only text that says WHAT is wrong — was dropped.
  const validator = Array.isArray(body?.errors)
    ? body.errors
        .filter((e) => e && typeof e === 'object')
        .map((e) => ({
          field: e.path || e.param || null,
          code: null,
          message: e.msg || e.message || null,
          value: null,
        }))
        .filter((e) => e.field || e.message)
    : [];

  // The declared shape wins per field when an endpoint somehow sends both.
  const seen = new Set(declared.map((f) => f.field));
  const extra = [...legacy, ...validator];
  const fieldErrors = [...declared, ...extra.filter((f) => !seen.has(f.field))];

  const errorIsCode = typeof body?.error === 'string' && CODE_SHAPE.test(body.error);
  // A real axios transport error carries `code` (ECONNABORTED, ERR_NETWORK), so
  // the error object itself became `body` above and its own English message
  // ("Network Error", "timeout of 20000ms exceeded") arrived here as if the
  // server had said it. Filter it the same way as the fallback below.
  const serverTextRaw = (errorIsCode ? null : str(body?.error)) || str(body?.message) || null;
  const serverText = serverTextRaw && BARE_STATUS.test(serverTextRaw) ? null : serverTextRaw;
  const fallback = BARE_STATUS.test(String(err.message || '')) ? null : str(err.message);

  return {
    status,
    code: body?.code || (errorIsCode ? body.error : null),
    message: serverText || fallback,
    fieldErrors,
    // Values the fixed sentence needs to stay useful. A translated key REPLACES
    // the server's own English, so a message that said "unpaid balance 450.00
    // SAR" collapses to "the job card state does not allow this" unless the
    // number travels separately and the key interpolates it.
    data:
      body?.data && typeof body.data === 'object' && !Array.isArray(body.data) ? body.data : null,
  };
};

/**
 * The sentence to show. `t` is react-i18next's translator.
 *
 * A key is used only when it genuinely exists: `i18n.exists` is checked because
 * a missing key here would render as a humanised key name and read like a real
 * message, hiding an untranslated error instead of surfacing it.
 */
export const errorText = (t, err, fallbackKey = 'err.unexpected') => {
  const { code, message, fieldErrors, data: rawData } = apiError(err);
  let data = code === 'PERMISSION_DENIED' ? permissionDeniedValues(t, rawData) : rawData;
  // PACKAGE_PARTS_MISSING names its parts in both languages (`list` /
  // `list_ar`); the sentence has one {{list}} placeholder, filled in the
  // language the screen is in.
  if (code === 'PACKAGE_PARTS_MISSING' && data && data.list_ar && uiIsArabic()) {
    data = { ...data, list: data.list_ar };
  }

  const fromCode = (c) => {
    if (!c) return null;
    const key = `err.${c}`;
    // Asked of the i18n INSTANCE, not of `t`. react-i18next's `t` carries no
    // `.i18n` property — reading it returned undefined, `exists` was never
    // called, and errorText silently never translated: the whole Arabic path
    // was dead while every test passed, because the tests' fake `t` had `.i18n`
    // bolted on. A helper must be probed against the object the app really
    // passes it.
    // `data` is passed as interpolation values. A key with no placeholders
    // ignores them, so this is safe for every code, not just the ones that
    // interpolate.
    if (!keyExists(key)) return null;
    const text = t(key, data || undefined);
    // An unresolved placeholder means the values did not arrive — an older
    // route, a proxy that stripped the body, a key that outran its call site.
    // The server's own sentence is worse than the translation but far better
    // than showing a user "{{reason}}", so it wins in that one case.
    return typeof text === 'string' && text.includes('{{') ? null : text;
  };

  // ATTACHMENT_INVALID carries { index, reason } (and the damage point's own
  // on-screen number as `marker`): say WHICH entry and WHY. Only when both
  // keys exist — an untranslated reason keeps the plain sentence below.
  if (code === 'ATTACHMENT_INVALID' && data && typeof data.reason === 'string') {
    const reasonKey = `err.attachmentReason.${data.reason}`;
    const hasMarker = data.marker !== undefined && data.marker !== null;
    const atKey = hasMarker ? 'err.ATTACHMENT_INVALID_MARKER' : 'err.ATTACHMENT_INVALID_AT';
    const n = hasMarker ? data.marker : Number(data.index) + 1;
    if (keyExists(atKey) && keyExists(reasonKey) && Number.isFinite(Number(n))) {
      return t(atKey, { n, reason: t(reasonKey) });
    }
  }

  // VALIDATION_FAILED says "check the highlighted fields". On a surface that
  // only toasts — no fieldErrorMap — nothing IS highlighted, so the one field
  // message the server sent is strictly more useful than the generic sentence.
  const genericValidation = code === 'VALIDATION_FAILED' && fieldErrors.length === 1;
  const top = genericValidation ? null : fromCode(code);
  if (top) return top;

  // A single field error IS the message when the server sent no top-level code:
  // "Commercial registration must be exactly 10 digits" beats "Validation failed".
  if (fieldErrors.length === 1) {
    const only = fromCode(fieldErrors[0].code) || str(fieldErrors[0].message);
    if (only) return only;
  }

  if (message) return message;
  return t(fallbackKey);
};

/**
 * Field name -> message, ready to hand to <FormField error={...}>.
 * Falls back to the server's English per field when that code has no key.
 */
export const fieldErrorMap = (t, err) => {
  const { fieldErrors, data } = apiError(err);
  const map = {};
  fieldErrors.forEach((f) => {
    if (!f.field) return;
    const values = { ...(data || {}), ...(f.data || {}) };
    // Two contexts, one code. In a toast the message stands alone and has to
    // name itself ("Required field is missing."); under a labelled input the
    // label already said which field it is, so the same sentence reads like
    // stray prose. `err.field.CODE` carries the short form and is optional —
    // a code with no short form keeps its full sentence, which is right for
    // anything already specific (err.SUPPLIER_REQUIRED = "Select the supplier").
    const short = `err.field.${f.code}`;
    const key = f.code && keyExists(short) ? short : `err.${f.code}`;
    const text = f.code && keyExists(key) ? t(key, values) : null;
    // Same guard errorText carries: a sentence that interpolates renders its
    // literal braces when `data` did not travel with the code. "{{remaining}}"
    // under a payment box is worse than the server's own English.
    const resolved = typeof text === 'string' && !text.includes('{{') ? text : null;
    map[f.field] = resolved || f.message;
  });
  return map;
};

/**
 * Put a PERMISSION_DENIED refusal's `error` into the user's language, in place.
 *
 * Called from the axios response interceptor. About twenty screens print
 * `response.data.error` directly instead of going through errorText, and ~30
 * reducers store it via rejectBody — all of those would keep showing the
 * server's English ("You do not have permission to read reports"). Rewriting
 * the one field every one of them reads fixes them at the source instead of
 * site by site.
 *
 * errorText is unaffected: it reads the CODE, not this field, so a screen that
 * uses it still re-translates at render time if the language changes.
 */
export const localizePermissionDenied = (t, error) => {
  const body = error?.response?.data;
  if (error?.response?.status !== 403 || !body || body.code !== 'PERMISSION_DENIED') return;
  body.error = errorText(t, error);
};

export default apiError;

/**
 * The body to hand rejectWithValue from a thunk's catch.
 *
 * A thunk that rejects with `error.response?.data?.error` throws away the two
 * fields this whole contract exists for: `code` (which the portal translates)
 * and `fieldErrors` (which highlights the inputs). Rejecting with the BODY
 * keeps both. A non-object body — nginx answers a 502 with an HTML page — is
 * wrapped, because a bare string has no `.error` for the reducer to read and
 * lands as RTK's literal "Rejected".
 */
export const rejectBody = (err, fallback) => {
  const body = err?.response?.data;
  if (body && typeof body === 'object') return body;
  // "Request failed with status code 502" is not an explanation. apiError
  // filters it out of err.message but cannot see it once it has been wrapped as
  // body.error, so it has to be filtered here, at the wrap.
  const raw = err?.message || null;
  return { error: fallback || (BARE_STATUS.test(String(raw || '')) ? null : raw) };
};
